import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle, Download, ArrowLeft, FileText, Scale, AlertCircle as AlertIcon, Clock, Hash, ChevronDown, ChevronUp, Globe, Layers, XCircle, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { RadialBarChart, RadialBar, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';

// ALWAYS use relative URL - same-origin requests only
const API = '/api';

const SEAL_URL = "https://static.prod-images.emergentagent.com/jobs/81aa82bc-64f4-44c1-9b2b-dd61a57bef5d/images/3e285f76a4297c4321e05dd126ba1e8a288cb1bae9d50c956b95e8cb18372361.png";

// Risk classification mapping (replaces PASS/FAIL)
const getRiskClassification = (status, riskScore) => {
  if (status?.toUpperCase() === 'PASS' || riskScore <= 25) return 'LOW RISK';
  if (riskScore <= 50) return 'MODERATE RISK';
  if (riskScore <= 75) return 'ELEVATED RISK';
  return 'HIGH RISK';
};

const getRiskClassificationColor = (classification) => {
  switch (classification) {
    case 'LOW RISK': return '#16a34a';
    case 'MODERATE RISK': return '#ca8a04';
    case 'ELEVATED RISK': return '#ea580c';
    case 'HIGH RISK': return '#dc2626';
    default: return '#6b7280';
  }
};

const getRiskClassificationBg = (classification) => {
  switch (classification) {
    case 'LOW RISK': return 'bg-green-50 border-green-500 dark:bg-green-950';
    case 'MODERATE RISK': return 'bg-yellow-50 border-yellow-500 dark:bg-yellow-950';
    case 'ELEVATED RISK': return 'bg-orange-50 border-orange-500 dark:bg-orange-950';
    case 'HIGH RISK': return 'bg-red-50 border-red-500 dark:bg-red-950';
    default: return 'bg-muted/50 border-muted';
  }
};

const ScanResults = ({ user: propsUser }) => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(propsUser);
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedJurisdictions, setExpandedJurisdictions] = useState({});
  const [expandedPlatforms, setExpandedPlatforms] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userPromise = propsUser ? Promise.resolve({ data: propsUser }) : axios.get(`${API}/auth/me`, { withCredentials: true });
        const scanPromise = axios.get(`${API}/scan/${scanId}`, { withCredentials: true });

        const [userRes, scanRes] = await Promise.all([userPromise, scanPromise]);
        setUser(userRes.data);
        setScan(scanRes.data);
        
        // Auto-expand all sections for better UX
        const jExpanded = {};
        const pExpanded = {};
        scanRes.data.jurisdiction_results?.forEach((_, i) => { jExpanded[i] = true; });
        scanRes.data.platform_results?.forEach((_, i) => { pExpanded[i] = true; });
        setExpandedJurisdictions(jExpanded);
        setExpandedPlatforms(pExpanded);
      } catch (error) {
        toast.error('Failed to load scan results');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [scanId, propsUser, navigate]);

  const toggleJurisdiction = (index) => {
    setExpandedJurisdictions(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const togglePlatform = (index) => {
    setExpandedPlatforms(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const getRiskColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'moderate': return '#ca8a04';
      case 'low': return '#16a34a';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const downloadPDF = async () => {
    if (!scan) return;
    setDownloadingPdf(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      let yPos = 20;

      // Header
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 51, 102);
      pdf.text('ScanMyAd', margin, yPos);
      
      yPos += 8;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Pre-Publication Regulatory Risk Assessment', margin, yPos);

      // Report metadata
      yPos += 15;
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      pdf.text(`Report ID: ${scan.scan_id}`, margin, yPos);
      yPos += 5;
      pdf.text(`Generated: ${formatDate(scan.created_at)}`, margin, yPos);
      yPos += 5;
      pdf.text(`Jurisdictions: ${scan.target_jurisdictions?.join(', ')}`, margin, yPos);
      yPos += 5;
      pdf.text(`Platforms: ${scan.target_platforms?.join(', ')}`, margin, yPos);

      // Ad Content Analyzed Section
      yPos += 15;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Ad Content Analyzed', margin, yPos);
      
      yPos += 6;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Original Advertising Copy Submitted for Risk Assessment', margin, yPos);
      
      yPos += 8;
      // Draw light grey box for ad content
      const adContent = scan.ad_content || scan.input_text || scan.adCopy || scan.ad_copy || scan.ad_text || scan.content || 'No ad content available';
      pdf.setFont('courier', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(40, 40, 40);
      const adLines = pdf.splitTextToSize(adContent, pageWidth - margin * 2 - 10);
      const boxHeight = Math.min(adLines.length * 5 + 10, 60);
      pdf.setFillColor(245, 245, 245);
      pdf.rect(margin, yPos - 2, pageWidth - margin * 2, boxHeight, 'F');
      pdf.text(adLines.slice(0, 10), margin + 5, yPos + 4);
      if (adLines.length > 10) {
        pdf.text('...', margin + 5, yPos + boxHeight - 6);
      }
      yPos += boxHeight + 4;
      
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text('Note: This risk assessment is based on the advertising text exactly as submitted.', margin, yPos);
      yPos += 4;
      pdf.text('Edits after this scan may change results.', margin, yPos);

      // Overall Risk Score
      yPos += 12;
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('AI Risk Assessment Summary', margin, yPos);
      
      yPos += 10;
      pdf.setFontSize(36);
      pdf.setTextColor(...(scan.risk_level?.toLowerCase() === 'low' ? [22, 163, 74] : scan.risk_level?.toLowerCase() === 'critical' ? [220, 38, 38] : [202, 138, 4]));
      pdf.text(`${scan.risk_score}/100`, margin, yPos);
      
      pdf.setFontSize(14);
      pdf.text(`${scan.risk_level?.toUpperCase()} RISK`, margin + 50, yPos);

      // Risk score clarification
      yPos += 12;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(100, 100, 100);
      const clarificationText = 'Risk score reflects AI-detected probability of regulatory or platform review friction based on analyzed language patterns. This is not a legal determination.';
      const clarificationLines = pdf.splitTextToSize(clarificationText, pageWidth - margin * 2);
      pdf.text(clarificationLines, margin, yPos);
      yPos += clarificationLines.length * 4;

      // Jurisdiction Results
      yPos += 15;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Jurisdiction-by-Jurisdiction Analysis', margin, yPos);

      scan.jurisdiction_results?.forEach((jr, idx) => {
        yPos += 12;
        if (yPos > 260) {
          pdf.addPage();
          yPos = 20;
        }
        
        const riskClass = getRiskClassification(jr.status, jr.risk_score || 50);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        const statusColor = riskClass === 'LOW RISK' ? [22, 163, 74] : riskClass === 'HIGH RISK' ? [220, 38, 38] : [202, 138, 4];
        pdf.setTextColor(...statusColor);
        pdf.text(`${jr.jurisdiction}: ${riskClass}`, margin, yPos);
        
        if (jr.violations?.length > 0) {
          jr.violations.forEach((v, i) => {
            yPos += 6;
            if (yPos > 260) { pdf.addPage(); yPos = 20; }
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(80, 80, 80);
            const violationText = `• ${v.regulation || v}: ${v.issue || ''}`;
            const lines = pdf.splitTextToSize(violationText, pageWidth - margin * 2);
            pdf.text(lines, margin + 5, yPos);
            yPos += (lines.length - 1) * 4;
          });
        } else if (jr.pass_reasons?.length > 0) {
          jr.pass_reasons.forEach((reason) => {
            yPos += 6;
            if (yPos > 260) { pdf.addPage(); yPos = 20; }
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(22, 163, 74);
            pdf.text(`• ${reason}`, margin + 5, yPos);
          });
        }
      });

      // Platform Results
      yPos += 15;
      if (yPos > 240) { pdf.addPage(); yPos = 20; }
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Platform-by-Platform Analysis', margin, yPos);

      scan.platform_results?.forEach((pr) => {
        yPos += 12;
        if (yPos > 260) { pdf.addPage(); yPos = 20; }
        
        const riskClass = getRiskClassification(pr.status, 50);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        const statusColor = riskClass === 'LOW RISK' ? [22, 163, 74] : riskClass === 'HIGH RISK' ? [220, 38, 38] : [202, 138, 4];
        pdf.setTextColor(...statusColor);
        pdf.text(`${pr.platform}: ${riskClass}`, margin, yPos);
        
        const items = pr.status?.toUpperCase() === 'PASS' ? pr.pass_reasons : pr.violations;
        items?.forEach((item) => {
          yPos += 6;
          if (yPos > 260) { pdf.addPage(); yPos = 20; }
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(pr.status?.toUpperCase() === 'PASS' ? 22 : 220, pr.status?.toUpperCase() === 'PASS' ? 163 : 38, pr.status?.toUpperCase() === 'PASS' ? 74 : 38);
          pdf.text(`• ${item}`, margin + 5, yPos);
        });
      });

      // Suggested Rewrite
      if (scan.suggested_rewrite) {
        yPos += 20;
        if (yPos > 200) { pdf.addPage(); yPos = 20; }
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(22, 163, 74);
        pdf.text('Lower-Risk Suggested Revision', margin, yPos);
        
        yPos += 8;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        const rewriteLines = pdf.splitTextToSize(scan.suggested_rewrite, pageWidth - margin * 2);
        pdf.text(rewriteLines, margin, yPos);
      }

      // Assessment Methodology
      pdf.addPage();
      yPos = 20;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Assessment Methodology (Summary)', margin, yPos);
      
      yPos += 10;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      const methodologyText = 'This AI review compares submitted content against selected publicly available regulatory guidance and platform advertising policies. The system identifies potential risk signals including absolute claims, unqualified guarantees, sensitive claim categories, and implied endorsements.';
      const methodologyLines = pdf.splitTextToSize(methodologyText, pageWidth - margin * 2);
      pdf.text(methodologyLines, margin, yPos);

      // Legal Disclaimer
      yPos += methodologyLines.length * 5 + 15;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Legal Disclaimer', margin, yPos);
      
      yPos += 10;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      const disclaimerLines = pdf.splitTextToSize(scan.legal_disclaimer_text || 'This report is for informational purposes only and does not constitute legal advice.', pageWidth - margin * 2);
      pdf.text(disclaimerLines, margin, yPos);

      // Footer with share hooks
      yPos = 265;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 51, 102);
      pdf.text('Generated with ScanMyAd — AI Pre-Publication Advertising Risk Assessment', margin, yPos);
      
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Run your own scan: https://www.scanmyad.com', margin, yPos);
      
      yPos += 5;
      pdf.text('Check another ad: https://www.scanmyad.com/scanner', margin, yPos);
      
      yPos += 8;
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text('BC Leads Business Services | Surrey, BC, Canada', margin, yPos);

      // Save PDF
      pdf.save(`ScanMyAd-Report-${scan.scan_id}.pdf`);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!scan) return null;

  const jurisdictions = Array.isArray(scan.target_jurisdictions) ? scan.target_jurisdictions : [scan.target_jurisdiction || scan.target_jurisdictions];
  const platforms = Array.isArray(scan.target_platforms) ? scan.target_platforms : [scan.target_platform || scan.target_platforms];
  const jurisdictionResults = scan.jurisdiction_results || [];
  const platformResults = scan.platform_results || [];

  const lowRiskJurisdictions = jurisdictionResults.filter(j => j.status?.toUpperCase() === 'PASS' || (j.risk_score && j.risk_score <= 25)).length;
  const lowRiskPlatforms = platformResults.filter(p => p.status?.toUpperCase() === 'PASS').length;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-muted/10 to-background">
      <Navbar user={user} />

      <main className="flex-1 py-8 px-4 md:px-6">
        <div className="container mx-auto max-w-6xl">
          {/* Header Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <Button onClick={downloadPDF} disabled={downloadingPdf} data-testid="download-pdf-btn" className="bg-primary">
              <Download className="mr-2 h-4 w-4" />
              {downloadingPdf ? 'Generating PDF...' : 'Download PDF Report'}
            </Button>
          </div>

          {/* Report Header with Logo */}
          <Card className="mb-6 border-2 shadow-lg" data-testid="report-header">
            <CardContent className="pt-6 pb-4">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Shield className="h-14 w-14 text-primary" />
                  <div>
                    <h1 className="font-heading text-2xl md:text-3xl font-bold">Pre-Publication Regulatory Risk Assessment</h1>
                    <p className="text-sm text-muted-foreground">AI-Powered Multi-Jurisdiction Analysis</p>
                  </div>
                </div>
                <img src={SEAL_URL} alt="AI Risk Assessment" className="h-16 w-16 opacity-80" />
              </div>

              <Separator className="my-4" />

              {/* Report Metadata */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-muted/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Hash className="h-3 w-3" />
                    <span className="text-xs font-medium">Report ID</span>
                  </div>
                  <div className="font-mono text-xs font-semibold truncate" data-testid="scan-id-display">{scan.scan_id}</div>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Clock className="h-3 w-3" />
                    <span className="text-xs font-medium">Generated</span>
                  </div>
                  <div className="text-xs font-semibold" data-testid="scan-created-time">{formatDate(scan.created_at)}</div>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1 font-medium">Jurisdictions</div>
                  <div className="flex flex-wrap gap-1">
                    {jurisdictions.map((j, i) => (
                      <span key={i} className="inline-block px-2 py-0.5 bg-primary/10 text-primary text-xs rounded font-semibold">{j}</span>
                    ))}
                  </div>
                </div>
                <div className="bg-muted/50 p-3 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1 font-medium">Platforms</div>
                  <div className="flex flex-wrap gap-1">
                    {platforms.map((p, i) => (
                      <span key={i} className="inline-block px-2 py-0.5 bg-primary/10 text-primary text-xs rounded font-semibold">{p}</span>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ad Content Analyzed Section */}
          <Card className="mb-6 border-2" data-testid="ad-content-analyzed">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Ad Content Analyzed
              </CardTitle>
              <CardDescription>Original Advertising Copy Submitted for Risk Assessment</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/50 border rounded-lg p-4">
                <pre className="whitespace-pre-wrap font-mono text-sm text-foreground leading-relaxed" data-testid="ad-copy-display">
                  {scan.ad_content || scan.input_text || scan.adCopy || scan.ad_copy || scan.ad_text || scan.content || 'No ad content available'}
                </pre>
              </div>
              <p className="text-xs text-muted-foreground mt-3 italic">
                Note: This risk assessment is based on the advertising text exactly as submitted. Edits after this scan may change results.
              </p>
            </CardContent>
          </Card>

          {/* AI Risk Assessment Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card className="border-2" style={{ borderColor: getRiskColor(scan.risk_level) }}>
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold" style={{ color: getRiskColor(scan.risk_level) }} data-testid="risk-score-value">
                  {scan.risk_score}
                </div>
                <div className="text-xs text-muted-foreground">Risk Score</div>
                <Badge variant="outline" className="mt-1" style={{ borderColor: getRiskColor(scan.risk_level), color: getRiskColor(scan.risk_level) }} data-testid="risk-level-badge">
                  {scan.risk_level?.toUpperCase()} RISK
                </Badge>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-500">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{lowRiskJurisdictions}/{jurisdictionResults.length}</div>
                <div className="text-xs text-muted-foreground">Low Risk Regions</div>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-500">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{lowRiskPlatforms}/{platformResults.length}</div>
                <div className="text-xs text-muted-foreground">Low Risk Platforms</div>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-primary">{jurisdictionResults.length * platformResults.length || 1}</div>
                <div className="text-xs text-muted-foreground">Total Checks</div>
              </CardContent>
            </Card>
          </div>

          {/* Risk Score Clarification */}
          <Card className="mb-6 border border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
            <CardContent className="py-3 px-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Risk Score Clarification:</strong> Risk score reflects AI-detected probability of regulatory or platform review friction based on analyzed language patterns. This is not a legal determination.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* JURISDICTION-BY-JURISDICTION BREAKDOWN */}
          <Card className="mb-6 border-2 shadow-lg" data-testid="jurisdiction-results-card">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-background pb-4">
              <CardTitle className="flex items-center text-xl">
                <Globe className="mr-3 h-6 w-6 text-primary" />
                Jurisdiction-by-Jurisdiction Risk Analysis
              </CardTitle>
              <CardDescription>Risk assessment for each regulatory region</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {jurisdictionResults.length > 0 ? (
                <div className="space-y-4">
                  {jurisdictionResults.map((jr, index) => {
                    const riskClass = getRiskClassification(jr.status, jr.risk_score || 50);
                    const riskColor = getRiskClassificationColor(riskClass);
                    return (
                      <div 
                        key={index} 
                        className={`border-2 rounded-lg overflow-hidden ${getRiskClassificationBg(riskClass)}`}
                        data-testid={`jurisdiction-result-${index}`}
                      >
                        <div 
                          className="p-4 cursor-pointer hover:bg-black/5 transition-colors"
                          onClick={() => toggleJurisdiction(index)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {riskClass === 'LOW RISK' ? (
                                <CheckCircle className="h-8 w-8 text-green-600" />
                              ) : riskClass === 'HIGH RISK' ? (
                                <XCircle className="h-8 w-8 text-red-600" />
                              ) : (
                                <AlertTriangle className="h-8 w-8 text-orange-500" />
                              )}
                              <div>
                                <h3 className="font-heading font-bold text-lg">{jr.jurisdiction}</h3>
                                <span className="text-sm" style={{ color: riskColor }}>
                                  {riskClass} {jr.risk_score && `• Score: ${jr.risk_score}/100`}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge 
                                variant="outline"
                                className="text-sm px-3 py-1"
                                style={{ borderColor: riskColor, color: riskColor }}
                              >
                                {riskClass === 'LOW RISK' ? 'No High-Risk Flags Detected' : `${jr.violations?.length || 0} Risk Signals`}
                              </Badge>
                              {expandedJurisdictions[index] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                            </div>
                          </div>
                        </div>

                        {expandedJurisdictions[index] && (
                          <div className="px-4 pb-4 space-y-3 border-t">
                            {riskClass !== 'LOW RISK' && jr.violations?.length > 0 ? (
                              <div className="mt-3">
                                <h4 className="font-semibold text-sm text-red-700 mb-2">Risk Signals Identified:</h4>
                                {jr.violations.map((v, vi) => (
                                  <div key={vi} className="bg-white/80 dark:bg-black/20 p-3 rounded-md mb-2 border-l-4 border-red-500">
                                    <div className="font-semibold text-sm">{v.regulation || v}</div>
                                    {v.issue && <p className="text-sm text-muted-foreground mt-1">{v.issue}</p>}
                                    {v.severity && (
                                      <Badge variant="outline" className="mt-1 text-xs" style={{ borderColor: getRiskColor(v.severity), color: getRiskColor(v.severity) }}>
                                        {v.severity}
                                      </Badge>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : jr.pass_reasons?.length > 0 ? (
                              <div className="mt-3">
                                <h4 className="font-semibold text-sm text-green-700 mb-2">Low Risk Indicators Identified:</h4>
                                {jr.pass_reasons.map((reason, ri) => (
                                  <div key={ri} className="flex items-start gap-2 mb-2">
                                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">{reason}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground mt-3">No detailed analysis available.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-6">No jurisdiction-specific results available.</p>
              )}
            </CardContent>
          </Card>

          {/* PLATFORM-BY-PLATFORM BREAKDOWN */}
          <Card className="mb-6 border-2 shadow-lg" data-testid="platform-results-card">
            <CardHeader className="bg-gradient-to-r from-primary/5 to-background pb-4">
              <CardTitle className="flex items-center text-xl">
                <Layers className="mr-3 h-6 w-6 text-primary" />
                Platform-by-Platform Risk Analysis
              </CardTitle>
              <CardDescription>Policy risk assessment for each advertising platform</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {platformResults.length > 0 ? (
                <div className="space-y-4">
                  {platformResults.map((pr, index) => {
                    const riskClass = getRiskClassification(pr.status, 50);
                    const riskColor = getRiskClassificationColor(riskClass);
                    return (
                      <div 
                        key={index} 
                        className={`border-2 rounded-lg overflow-hidden ${getRiskClassificationBg(riskClass)}`}
                        data-testid={`platform-result-${index}`}
                      >
                        <div 
                          className="p-4 cursor-pointer hover:bg-black/5 transition-colors"
                          onClick={() => togglePlatform(index)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {riskClass === 'LOW RISK' ? (
                                <CheckCircle className="h-8 w-8 text-green-600" />
                              ) : riskClass === 'HIGH RISK' ? (
                                <XCircle className="h-8 w-8 text-red-600" />
                              ) : (
                                <AlertTriangle className="h-8 w-8 text-orange-500" />
                              )}
                              <div>
                                <h3 className="font-heading font-bold text-lg">{pr.platform}</h3>
                                <span className="text-sm" style={{ color: riskColor }}>
                                  {riskClass}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge 
                                variant="outline"
                                className="text-sm px-3 py-1"
                                style={{ borderColor: riskColor, color: riskColor }}
                              >
                                {riskClass === 'LOW RISK' ? 'No High-Risk Flags Detected' : `${pr.violations?.length || 0} Risk Signals`}
                              </Badge>
                              {expandedPlatforms[index] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                            </div>
                          </div>
                        </div>

                        {expandedPlatforms[index] && (
                          <div className="px-4 pb-4 space-y-3 border-t">
                            {riskClass !== 'LOW RISK' && pr.violations?.length > 0 ? (
                              <div className="mt-3">
                                <h4 className="font-semibold text-sm text-red-700 mb-2">Policy Risk Signals:</h4>
                                {pr.violations.map((v, vi) => (
                                  <div key={vi} className="flex items-start gap-2 mb-2">
                                    <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">{v}</span>
                                  </div>
                                ))}
                              </div>
                            ) : pr.pass_reasons?.length > 0 ? (
                              <div className="mt-3">
                                <h4 className="font-semibold text-sm text-green-700 mb-2">Low Risk Indicators Identified:</h4>
                                {pr.pass_reasons.map((reason, ri) => (
                                  <div key={ri} className="flex items-start gap-2 mb-2">
                                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm">{reason}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground mt-3">No detailed policy analysis available.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-6">No platform-specific results available.</p>
              )}
            </CardContent>
          </Card>

          {/* Lower-Risk Suggested Revision (formerly AI-Generated Compliant Rewrite) */}
          {scan.suggested_rewrite && (
            <Card className="mb-6 border-2 border-green-500 shadow-lg" data-testid="suggested-rewrite-card">
              <CardHeader className="bg-green-50 dark:bg-green-950 pb-4">
                <CardTitle className="flex items-center text-xl text-green-700">
                  <CheckCircle className="h-6 w-6 mr-3" />
                  Lower-Risk Suggested Revision
                </CardTitle>
                <CardDescription>AI-suggested revision to reduce identified risk signals across jurisdictions and platforms</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="p-4 bg-green-50 dark:bg-green-950 border-2 border-green-400 rounded-lg text-sm leading-relaxed">
                  {scan.suggested_rewrite}
                </div>
                <div className="mt-3 p-3 bg-muted/50 rounded-md border-l-4 border-green-500">
                  <p className="text-xs text-muted-foreground">
                    <strong className="text-foreground">Important:</strong> This is an AI-generated suggestion intended to reduce potential risk signals. Review with qualified legal counsel and adapt to your brand voice before use.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assessment Methodology */}
          <Card className="mb-6 border shadow-md" data-testid="methodology-card">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Info className="h-5 w-5 mr-2 text-muted-foreground" />
                Assessment Methodology (Summary)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This AI review compares submitted content against selected publicly available regulatory guidance and platform advertising policies. The system identifies potential risk signals including absolute claims, unqualified guarantees, sensitive claim categories, and implied endorsements.
              </p>
            </CardContent>
          </Card>

          {/* Legal Disclaimer */}
          <Card className="border-2 border-muted/50 bg-muted/20" data-testid="bottom-disclaimer">
            <CardContent className="pt-6 pb-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <img src={SEAL_URL} alt="AI Risk Assessment" className="h-20 w-20 opacity-80" />
                <Separator className="w-24" />
                <div className="max-w-3xl">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Scale className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-heading text-lg font-bold">Legal Disclaimer</h3>
                  </div>
                  {scan.legal_disclaimer_text && (
                    <div className="text-left bg-background/50 p-4 rounded-lg border">
                      <pre className="whitespace-pre-wrap text-xs leading-relaxed font-sans text-muted-foreground">
                        {scan.legal_disclaimer_text}
                      </pre>
                    </div>
                  )}
                </div>
                <Separator />
                <div className="text-center space-y-2">
                  <p className="text-sm font-bold text-primary">Generated with ScanMyAd — AI Pre-Publication Advertising Risk Assessment</p>
                  <p className="text-sm text-muted-foreground">Run your own scan: <a href="https://www.scanmyad.com" className="text-primary hover:underline">https://www.scanmyad.com</a></p>
                  <div className="pt-2">
                    <a href="/scanner" className="inline-flex items-center text-sm text-primary hover:underline font-medium">
                      Check another ad →
                    </a>
                  </div>
                  <Separator className="my-3" />
                  <p className="text-xs text-muted-foreground">BC Leads Business Services | Surrey, BC, Canada</p>
                  <p className="text-xs text-muted-foreground">Report ID: {scan.scan_id}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ScanResults;
